// AI Suggested Next Steps card — shown inside the incident detail panel
// for the first 24h after creation. escalation_manager+ only. Dismissible.
//
// Architecture notes:
//  • The fetch is deliberately NOT done in useEffect — React StrictMode double-
//    invokes effects which made earlier useRef-based one-shot guards
//    insufficient (cleanup race + setState skipped via stale `cancelled`).
//  • Instead we rely on backend persistence (incident.ai_suggestions) + a
//    module-level dedupe Map keyed by incident.id so concurrent mounts share
//    the same in-flight Promise.
//  • The fetch is kicked off once per incident.id from a useEffect that has
//    only [incident.id, shouldShow] as deps and is idempotent thanks to the
//    module-level Map.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, X, ArrowRight, Clock } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { hasMinRole } from "./RoleGuard";
import { useStore } from "../store";
import { usePatchIncident } from "../api/sitroom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABEL = {
  open_8d: "Open 8D",
  service_request: "Add Request",
  comment: "Comment",
  escalate: "Escalate",
  contact_party: "@Mention party",
};

const TYPE_TINT = {
  open_8d: "#f3b14a",
  service_request: "#9ec3ec",
  comment: "#c89adb",
  escalate: "#E24B4A",
  contact_party: "#4ed084",
};

// Module-level dedupe — StrictMode-safe. Two parallel mounts of the same
// component (e.g. dev double-invoke) reuse the same Promise instead of
// firing two POSTs.
const _inflight = new Map(); // incidentId -> Promise<suggestions[]>

const fetchSuggestions = (incidentId) => {
  if (_inflight.has(incidentId)) return _inflight.get(incidentId);
  const promise = axios
    .post(`${API}/incidents/${incidentId}/suggest-next-steps`, {})
    .then((r) => r.data?.suggestions || [])
    .finally(() => {
      // Clear after a short delay so genuine retries can re-fetch
      setTimeout(() => _inflight.delete(incidentId), 1500);
    });
  _inflight.set(incidentId, promise);
  return promise;
};

export const AISuggestedNextSteps = ({ incident }) => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const patchIncidentMutation = usePatchIncident();
  const openNewEightDModal = useStore((s) => s.openNewEightDModal);
  const openSrModalWithType = useStore((s) => s.openSrModalWithType);
  const setCommentPrefill = useStore((s) => s.setCommentPrefill);
  const addToast = useStore((s) => s.addToast);

  // Role + 24h window guards
  const canView = hasMinRole(user?.role, "escalation_manager");
  const ageHours = incident.createdAt
    ? (Date.now() - new Date(incident.createdAt).getTime()) / 3600000
    : 0;
  const withinWindow = ageHours < 24;

  // Local state — initial values come straight from the persisted incident doc.
  const [suggestions, setSuggestions] = useState(incident.ai_suggestions || null);
  const [dismissed, setDismissed] = useState(Boolean(incident.ai_suggestions_dismissed));
  const [snoozedUntil, setSnoozedUntil] = useState(
    incident.ai_suggestions_snoozed_until
      ? new Date(incident.ai_suggestions_snoozed_until).getTime()
      : 0,
  );
  const [loading, setLoading] = useState(false);
  const [errorTxt, setErrorTxt] = useState(null);

  // 1-second tick to drive snooze re-render when window expires
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!snoozedUntil || snoozedUntil <= Date.now()) return undefined;
    const i = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(i);
  }, [snoozedUntil]);
  const isSnoozed = snoozedUntil > now;

  const shouldShow = canView && withinWindow && !dismissed && !isSnoozed;

  // Sync local state if the parent's incident prop changes (e.g. user reopens
  // a different incident, or React Query background-refetches the list).
  useEffect(() => {
    setSuggestions(incident.ai_suggestions || null);
    setDismissed(Boolean(incident.ai_suggestions_dismissed));
    setSnoozedUntil(
      incident.ai_suggestions_snoozed_until
        ? new Date(incident.ai_suggestions_snoozed_until).getTime()
        : 0,
    );
    setErrorTxt(null);
  }, [incident.id, incident.ai_suggestions, incident.ai_suggestions_dismissed, incident.ai_suggestions_snoozed_until]);

  // Kick off (or join) the fetch for this incident — idempotent thanks to the
  // module-level dedupe map. StrictMode-safe: parallel invocations share the
  // same Promise instead of firing parallel POSTs.
  useEffect(() => {
    if (!shouldShow) return;
    if (suggestions) return; // already have results (or empty array = "tried")
    if (loading) return;
    let mounted = true;
    setLoading(true);
    fetchSuggestions(incident.id)
      .then((s) => {
        if (!mounted) return;
        setSuggestions(s);
        qc.setQueryData(["incidents"], (old) =>
          Array.isArray(old)
            ? old.map((i) => (i.id === incident.id ? { ...i, ai_suggestions: s } : i))
            : old,
        );
      })
      .catch((e) => {
        if (!mounted) return;
        setErrorTxt(e?.response?.data?.detail || "Could not generate");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
    // We intentionally trigger only on id/visibility changes — `loading` and
    // `suggestions` are read inside but should not retrigger the effect.
     
  }, [incident.id, shouldShow]);

  const handleDismiss = async () => {
    setDismissed(true);
    try {
      await patchIncidentMutation.mutateAsync({
        id: incident.id,
        patch: { ai_suggestions_dismissed: true },
      });
    } catch (_) {
      // Local dismissal is enough for UX; backend best-effort
    }
  };

  const handleSnooze = async () => {
    const until = Date.now() + 3600000; // +1h
    setSnoozedUntil(until);
    const iso = new Date(until).toISOString();
    try {
      await patchIncidentMutation.mutateAsync({
        id: incident.id,
        patch: {
          ai_suggestions_snoozed_until: iso,
          ai_suggestions_snooze_notified: false,
        },
      });
      addToast({
        type: "info",
        title: "Snoozed for 1 hour",
        body: "You'll get a reminder when AI Next Steps are ready to review.",
      });
    } catch (_) {
      // Local snooze is best-effort; reload would lose it but UX is OK
    }
  };

  const handleAction = (s) => {
    switch (s.type) {
      case "open_8d":
        openNewEightDModal(incident.id);
        break;
      case "service_request":
        openSrModalWithType(incident.id, s.sr_type || null);
        break;
      case "comment":
        setCommentPrefill(incident.id, s.action || "", null);
        break;
      case "contact_party": {
        // Try to pull "Contact <Party Name>" out of the label; fall back to first linked party
        let mention = null;
        const m = (s.label || "").match(/contact\s+(.+)/i);
        if (m) mention = m[1].trim();
        if (!mention) {
          mention = (incident.linked_tenant_ids || [])[0] || (incident.linkedParties || [])[0]?.name || null;
        }
        setCommentPrefill(incident.id, s.action || "", mention);
        break;
      }
      case "escalate": {
        // Use the existing escalate flow — trip a PATCH that elevates severity OR
        // routes through the existing escalate confirmation. We surface a toast
        // and PATCH to status='escalated' which is observed by the SLA monitor.
        if (window.confirm(`Escalate ${incident.id}?\n\n${s.action}`)) {
          patchIncidentMutation.mutate(
            { id: incident.id, patch: { status: "escalated" } },
            {
              onSuccess: () => addToast({
                type: "s1", title: "Escalated", body: incident.id,
              }),
            },
          );
        }
        break;
      }
      default:
        break;
    }
  };

  if (!shouldShow) return null;

  return (
    <div
      className="rounded-lg mb-4"
      style={{
        background: "rgba(155,89,182,0.06)",
        border: "1px solid rgba(155,89,182,0.40)",
      }}
      data-testid="ai-next-steps-card"
    >
      <div
        className="flex items-center justify-between px-3 py-2"
        style={{ borderBottom: "1px solid rgba(155,89,182,0.18)" }}
      >
        <div className="flex items-center gap-2">
          <Sparkles size={12} style={{ color: "#c89adb" }} />
          <span
            style={{
              fontFamily: "Syne, sans-serif",
              fontWeight: 600,
              fontSize: 12,
              color: "#c89adb",
              letterSpacing: "0.4px",
            }}
          >
        ✦ SITREP suggests next steps
          </span>
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded ml-1"
            style={{
              background: "rgba(155,89,182,0.20)",
              color: "#c89adb",
              fontFamily: "'DM Mono', monospace",
              fontSize: 8,
              letterSpacing: "1.2px",
            }}
          >
            CLAUDE SONNET
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleSnooze}
            className="p-1 rounded transition-colors flex items-center gap-1 px-1.5"
            style={{
              background: "transparent",
              border: "1px solid rgba(155,89,182,0.30)",
              color: "#c89adb",
              cursor: "pointer",
              opacity: 0.85,
              fontFamily: "'DM Mono', monospace",
              fontSize: 9,
              letterSpacing: "0.4px",
            }}
            data-testid="ai-next-steps-snooze"
            title="Snooze for 1 hour"
          >
            <Clock size={10} /> 1h
          </button>
          <button
            onClick={handleDismiss}
            className="p-1 rounded transition-colors"
            style={{
              background: "transparent",
              border: "none",
              color: "#c89adb",
              cursor: "pointer",
              opacity: 0.7,
            }}
            data-testid="ai-next-steps-dismiss"
            aria-label="Dismiss AI suggestions"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      <div className="p-3">
        {loading && !suggestions && <ShimmerRows />}
        {errorTxt && !loading && !suggestions && (
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#c5cad8",
            }}
            data-testid="ai-next-steps-error"
          >
            Could not generate suggestions —{" "}
            <button
              onClick={handleDismiss}
              style={{
                background: "transparent",
                border: "none",
                color: "#9ec3ec",
                textDecoration: "underline",
                cursor: "pointer",
                padding: 0,
              }}
            >
              dismiss
            </button>
          </div>
        )}
        {Array.isArray(suggestions) && suggestions.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="ai-next-steps-list">
            {suggestions.map((s, idx) => (
              <SuggestionRow key={idx} s={s} onAction={() => handleAction(s)} />
            ))}
          </div>
        )}
        {Array.isArray(suggestions) && suggestions.length === 0 && !loading && (
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#9198b0",
            }}
            data-testid="ai-next-steps-empty"
          >
            No suggestions available for this incident.
          </div>
        )}
      </div>
    </div>
  );
};

const SuggestionRow = ({ s, onAction }) => {
  const tint = TYPE_TINT[s.type] || "#9198b0";
  return (
    <div
      className="flex items-start gap-3 p-2 rounded"
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
      data-testid={`ai-next-steps-row-${s.priority}`}
    >
      <span
        className="flex items-center justify-center rounded-full flex-shrink-0"
        style={{
          width: 22,
          height: 22,
          background: `${tint}25`,
          border: `1px solid ${tint}66`,
          color: tint,
          fontFamily: "Syne, sans-serif",
          fontWeight: 700,
          fontSize: 11,
        }}
      >
        {s.priority}
      </span>
      <div className="flex-1 min-w-0">
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 12,
            color: "#f0f2f8",
            lineHeight: 1.45,
          }}
        >
          {s.action}
        </div>
        {s.sr_type && (
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 9,
              color: "#9198b0",
              letterSpacing: "1.2px",
              textTransform: "uppercase",
              marginTop: 2,
            }}
          >
            SR · {s.sr_type}
          </div>
        )}
      </div>
      <button
        onClick={onAction}
        className="flex items-center gap-1 px-2 py-1 rounded flex-shrink-0 transition-colors"
        style={{
          background: `${tint}15`,
          border: `1px solid ${tint}55`,
          color: tint,
          fontFamily: "Syne, sans-serif",
          fontWeight: 600,
          fontSize: 11,
          letterSpacing: "0.4px",
          cursor: "pointer",
        }}
        data-testid={`ai-next-steps-action-${s.priority}`}
      >
        {s.label || TYPE_LABEL[s.type] || "Take action"} <ArrowRight size={11} />
      </button>
    </div>
  );
};

const ShimmerRows = () => (
  <div className="flex flex-col gap-2" data-testid="ai-next-steps-shimmer">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="flex items-center gap-3 p-2 rounded"
        style={{
          background: "rgba(255,255,255,0.02)",
          border: "1px solid rgba(255,255,255,0.06)",
          height: 44,
        }}
      >
        <div
          className="rounded-full shimmer"
          style={{ width: 22, height: 22 }}
        />
        <div className="flex-1 shimmer" style={{ height: 12, borderRadius: 3 }} />
      </div>
    ))}
    <style>{`
      @keyframes shimmer-loop {
        0% { background-position: -200px 0; }
        100% { background-position: 200px 0; }
      }
      .shimmer {
        background: linear-gradient(90deg,
          rgba(155,89,182,0.05) 0%,
          rgba(155,89,182,0.15) 50%,
          rgba(155,89,182,0.05) 100%);
        background-size: 400px 100%;
        animation: shimmer-loop 1.2s linear infinite;
      }
    `}</style>
  </div>
);
